"use client"

import { cn } from '@/lib/utils'
import { HugeiconsIcon } from "@hugeicons/react"
import { MinusSignIcon, AnalyticsDownIcon, AnalyticsUpIcon } from "@hugeicons/core-free-icons"

interface EnhancedMetricCardProps {
  label: string
  value: string | number
  icon?: React.ComponentType<{ className?: string }>
  trend?: {
    value: number
    isPositive: boolean
    comparisonLabel?: string
  }
  sparklineData?: Array<{ value: number }>
  onClick?: () => void
  isLive?: boolean
  className?: string
  subtitle?: string
  description?: string
  /** Selected/active state — thicker neutral border, not a color wash. */
  selected?: boolean
}

export default function EnhancedMetricCard({
  label,
  value,
  icon: Icon,
  trend,
  sparklineData,
  onClick,
  isLive = false,
  className,
  subtitle,
  description,
  selected = false,
}: EnhancedMetricCardProps) {
  
  const getTrendIcon = () => {
    if (!trend) return null
    if (trend.value === 0) return MinusSignIcon
    return trend.isPositive ? AnalyticsUpIcon : AnalyticsDownIcon
  }
  
  const getTrendColor = () => {
    if (!trend) return ''
    if (trend.value === 0) return 'text-muted-foreground'
    return trend.isPositive
      ? 'text-foreground'
      : 'text-muted-foreground'
  }
  
  const TrendIcon = getTrendIcon()
  
  // Generate sparkline SVG path
  const renderSparkline = () => {
    if (!sparklineData || sparklineData.length === 0) return null
    
    const width = 48
    const height = 16
    const padding = 2
    const chartWidth = width - padding * 2
    const chartHeight = height - padding * 2
    
    const values = sparklineData.map(d => d.value)
    const maxValue = Math.max(...values, 1)
    const minValue = Math.min(...values, 0)
    const range = maxValue - minValue || 1
    
    const points = values.map((val, index) => {
      const x = padding + (index / (values.length - 1 || 1)) * chartWidth
      const y = padding + chartHeight - ((val - minValue) / range) * chartHeight
      return `${x},${y}`
    }).join(' ')
    
    return (
      <svg width={width} height={height} className="shrink-0 opacity-60">
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-muted-foreground"
        />
      </svg>
    )
  }
  
  return (
    <div 
      className={cn(
        "group flex items-center gap-2 px-3 py-2 rounded-md bg-card border transition-all flex-1 min-w-0",
        selected
          ? "border-foreground/80"
          : "border-border/30 hover:bg-card hover:border-border/60",
        onClick && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {/* Label and Value */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-xs font-medium text-muted-foreground/80 truncate">
          {label}
        </span>
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-foreground tabular-nums leading-tight">
            {value}
          </span>
          {/* Trend indicator (if available) */}
          {trend && TrendIcon && (
            <div className={cn(
              "flex items-center gap-0.5 text-[11px] font-medium shrink-0",
              getTrendColor()
            )}>
              <HugeiconsIcon icon={TrendIcon} size={10} />
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
        {/* Subtitle */}
        {subtitle && (
          <span className="text-[11px] text-muted-foreground/70 truncate">
            {subtitle}
          </span>
        )}
      </div>
      
      {/* Sparkline */}
      {renderSparkline() && (
        <div className="shrink-0">
          {renderSparkline()}
        </div>
      )}
    </div>
  )
}

